import { accountsAPI } from '../lib/api';
import { getCurrentCompanyId } from '../utils/auth';

export const getChartOfAccounts = async (companyId = null) => {
  try {
    const actualCompanyId = companyId || getCurrentCompanyId();
    if (!actualCompanyId) {
      throw new Error('No company ID available');
    }
    
    const response = await accountsAPI.getAll(actualCompanyId);
    console.log('Raw accounts API response:', response.data);
    
    let accounts = [];
    if (response.data) {
      if (response.data.Success && Array.isArray(response.data.Data)) {
        accounts = response.data.Data;
      } else if (response.data.success && Array.isArray(response.data.data)) {
        accounts = response.data.data;
      } else if (Array.isArray(response.data)) {
        accounts = response.data;
      }
    }

    console.log('Processed accounts:', accounts);

    // Map API fields to expected field names
    const mappedAccounts = accounts.map(account => ({
      id: account.Id || account.id,
      code: account.AccountCode || account.accountCode || account.code,
      name: account.AccountName || account.accountName || account.name,
      type: account.AccountType || account.accountType || account.type,
      parentId: account.ParentId || account.parentId,
      balance: account.Balance || account.balance || 0,
      isActive: account.IsActive !== undefined ? account.IsActive : (account.isActive !== undefined ? account.isActive : true),
      description: account.Description || account.description,
      hasActivity: account.HasActivity || account.hasActivity || false,
      icon: getAccountIcon(account.AccountType || account.accountType || account.type),
      children: []
    }));

    // Build hierarchical structure
    const accountMap = {};
    const rootAccounts = [];

    mappedAccounts.forEach(account => {
      accountMap[account.id] = { ...account, children: [] };
    });

    mappedAccounts.forEach(account => {
      if (account.parentId) {
        const parent = accountMap[account.parentId];
        if (parent) {
          parent.children.push(accountMap[account.id]);
        }
      } else {
        rootAccounts.push(accountMap[account.id]);
      }
    });

    console.log('Final mapped accounts:', rootAccounts);
    return rootAccounts;
  } catch (error) {
    console.error('Error in getChartOfAccounts:', error);
    throw error;
  }
};

// Helper function to get account icon based on type
const getAccountIcon = (accountType) => {
  const icons = {
    'Asset': 'TrendingUp',
    'Liability': 'TrendingDown',
    'Equity': 'PieChart',
    'Income': 'DollarSign',
    'Revenue': 'DollarSign',
    'Expense': 'Receipt'
  };
  return icons[accountType] || 'Circle';
};

export const createAccount = async (accountData) => {
  try {
    const companyId = getCurrentCompanyId();
    if (!companyId) {
      throw new Error('No company ID available');
    }

    // Map the form data to the API payload format
    const payload = {
      companyId: companyId,
      accountCode: accountData.code,
      accountName: accountData.name,
      accountType: accountData.type,
      parentAccountId: accountData.parentId || null,
      isActive: accountData.isActive !== undefined ? accountData.isActive : true,
      description: accountData.description || '',
      balance: parseFloat(accountData.balance) || 0
    };

    console.log('Creating account with payload:', payload);
    const response = await accountsAPI.create(payload);
    
    // Map the response back to the expected format
    const createdAccount = response.data;
    return {
      id: createdAccount.Id || createdAccount.id,
      code: createdAccount.AccountCode || createdAccount.accountCode,
      name: createdAccount.AccountName || createdAccount.accountName,
      type: createdAccount.AccountType || createdAccount.accountType,
      parentId: createdAccount.ParentAccountId || createdAccount.parentAccountId,
      balance: createdAccount.Balance || createdAccount.balance || 0,
      isActive: createdAccount.IsActive !== undefined ? createdAccount.IsActive : createdAccount.isActive,
      description: createdAccount.Description || createdAccount.description,
      hasActivity: false,
      icon: getAccountIcon(createdAccount.AccountType || createdAccount.accountType),
      children: []
    };
  } catch (error) {
    console.error('Error creating account:', error);
    throw error;
  }
};

export const updateAccount = async (id, accountData) => {
  try {
    const companyId = getCurrentCompanyId();
    if (!companyId) {
      throw new Error('No company ID available');
    }

    // Map the form data to the API payload format
    const payload = {
      companyId: companyId,
      accountCode: accountData.code,
      accountName: accountData.name,
      accountType: accountData.type,
      parentAccountId: accountData.parentId || null,
      isActive: accountData.isActive !== undefined ? accountData.isActive : true,
      description: accountData.description || '',
      balance: parseFloat(accountData.balance) || 0
    };

    console.log('Updating account with payload:', payload);
    const response = await accountsAPI.update(id, payload);
    
    // Map the response back to the expected format
    const updatedAccount = response.data;
    return {
      id: updatedAccount.Id || updatedAccount.id || id,
      code: updatedAccount.AccountCode || updatedAccount.accountCode,
      name: updatedAccount.AccountName || updatedAccount.accountName,
      type: updatedAccount.AccountType || updatedAccount.accountType,
      parentId: updatedAccount.ParentAccountId || updatedAccount.parentAccountId,
      balance: updatedAccount.Balance || updatedAccount.balance || 0,
      isActive: updatedAccount.IsActive !== undefined ? updatedAccount.IsActive : updatedAccount.isActive,
      description: updatedAccount.Description || updatedAccount.description,
      hasActivity: updatedAccount.HasActivity || updatedAccount.hasActivity || false,
      icon: getAccountIcon(updatedAccount.AccountType || updatedAccount.accountType),
      children: accountData.children || []
    };
  } catch (error) {
    console.error('Error updating account:', error);
    throw error;
  }
};

export const deleteAccount = async (id) => {
  try {
    await accountsAPI.delete(id);
    return true;
  } catch (error) {
    console.error('Error deleting account:', error);
    throw error;
  }
};